/* oxlint-disable max-lines -- service method bindings are flat and cohesive; splitting would scatter closely-coupled call wrappers */
import { existsSync } from "node:fs";
import { fileURLToPath } from "node:url";
import { Worker } from "node:worker_threads";
import { io, type Socket } from "socket.io-client";
import type {
  AgentSessionServiceMethods,
  AgentMode,
  AgentQuestion,
  PtyChatEventPayload,
  RunnerMode,
  SocketResponse,
  WorkspaceDiscoveredPort,
} from "@project/shared";
import { fetchBootstrap, applyBootstrapToEnv } from "../setup/bootstrap.js";
import { pollUntilBound, PollUntilBoundHttpError } from "../setup/bootstrap-poll.js";

// ── Configuration ──────────────────────────────────────────────────────────

export interface AgentConnectionConfig {
  apiUrl: string;
  taskToken: string;
  sessionId: string;
  runnerMode?: RunnerMode;
}

// ── Incoming server-push event types ───────────────────────────────────────

export interface IncomingMessage {
  content: string;
  userId: string;
  source?: string;
  files?: Array<{ name: string; content: string; mimeType?: string }>;
  /**
   * Delivery hint for the PTY harness: "prefill" parks the message in the
   * Connected-TUI input for a human to review/edit/Enter instead of
   * auto-submitting (set by Refine → Discovery/Review). Absent → submit.
   */
  delivery?: "prefill";
}

export interface SetModeData {
  agentMode: AgentMode;
}

export interface ApiKeyUpdateData {
  apiKey: string;
  isSubscription?: boolean;
}

/**
 * Server push asking the builder pod to spawn a same-pod review child: a
 * second conveyor-agent process bound to a fresh review WorkspaceSession
 * (sessionId + sessionJwt). No taskId on the wire — the child inherits the
 * parent's CONVEYOR_TASK_ID and the server derives it from the JWT (SEC-9).
 */
export interface SpawnReviewData {
  sessionId: string;
  sessionJwt: string;
  mode: "code-review";
  branch?: string | null;
  prNumber?: number | null;
  checkoutRef?: string | null;
}

/**
 * Server push asking the builder pod to spawn an extra interactive child on
 * this pod: a second Claude TUI (mode "adhoc") or a raw login shell (mode
 * "shell"), each bound to a fresh WorkspaceSession (sessionId + sessionJwt).
 * No taskId on the wire — the child inherits the parent's CONVEYOR_TASK_ID
 * and the server derives it from the JWT (SEC-9).
 */
export interface SpawnTuiData {
  sessionId: string;
  sessionJwt: string;
  mode: "adhoc" | "shell";
  projectId: string;
}

// ── Connection class ───────────────────────────────────────────────────────

const EVENT_BATCH_MS = 500;
const MAX_EVENT_BUFFER = 5000;

// Task JWTs are minted with a 24h TTL (api: createTaskToken → createJWT(…, "24h")).
// Proactively re-mint from the bootstrap endpoint well inside that window so a
// long-running or restart-heavy task never reaches the API with an expired
// token (which surfaces to the agent as "Session not found" on its next RPC).
// 6h — comfortably inside the 24h TTL, with room for a few retries before expiry.
const TOKEN_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

export class AgentConnection {
  private socket: Socket | null = null;
  private readonly config: AgentConnectionConfig;
  private eventBuffer: Array<{ event: { type: string; [key: string]: unknown } }> = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private lastEmittedStatus: string | null = null;
  private lastReportedStatus: string | null = null;
  private droppedEventCount = 0;

  // Pending answer resolvers for askUserQuestion room-event fallback
  private pendingAnswerResolvers = new Map<string, (answers: Record<string, string>) => void>();

  // Dedup: suppress near-identical messages within a short window
  private recentMessages: Array<{ words: Set<string>; timestamp: number; preview: string }> = [];
  private static readonly DEDUP_WINDOW_MS = 30_000;
  private static readonly DEDUP_SIMILARITY_THRESHOLD = 0.7;
  private static readonly DEDUP_PREVIEW_LIMIT = 120;

  // Early-buffering: events that arrive before callbacks are registered
  private earlyMessages: IncomingMessage[] = [];
  private earlyStop = false;
  private earlySoftStop = false;
  private earlyModeChanges: SetModeData[] = [];

  // Registered callbacks
  private messageCallback: ((msg: IncomingMessage) => void) | null = null;
  private stopCallback: (() => void) | null = null;
  private softStopCallback: (() => void) | null = null;
  private modeChangeCallback: ((data: SetModeData) => void) | null = null;
  private apiKeyUpdateCallback: ((data: ApiKeyUpdateData) => void) | null = null;
  private pullBranchCallback: ((data: { branch: string }) => void) | null = null;
  private finalizeSnapshotCallback: (() => void) | null = null;
  private runStartCommandCallback: (() => void) | null = null;
  private earlyPullBranches: Array<{ branch: string }> = [];
  private spawnReviewCallback: ((data: SpawnReviewData) => void) | null = null;
  private earlySpawnReviews: SpawnReviewData[] = [];
  private spawnTuiCallback: ((data: SpawnTuiData) => void) | null = null;
  private earlySpawnTuis: SpawnTuiData[] = [];

  // PTY relay (S5 terminal). Single-slot callbacks, set per PtySession run.
  private ptyInputCallback: ((data: string) => void) | null = null;
  private ptyResizeCallback: ((cols: number, rows: number) => void) | null = null;

  constructor(config: AgentConnectionConfig) {
    this.config = config;
  }

  get sessionId(): string {
    return this.config.sessionId;
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  // ── Typed service method call ──────────────────────────────────────────

  // Socket.IO keeps the SAME Socket instance across transport-level
  // reconnects (it only goes null on an explicit disconnect() teardown), so a
  // brief flap leaves `this.socket` non-null but `.connected === false`. Rather
  // than failing a tool call instantly (which the spawned `claude` surfaces as
  // "Conveyor MCP disconnected" and an excuse to go idle), we wait out a short
  // reconnect window, then emit with an ack timeout so a buffered packet whose
  // ack never returns can't hang the call forever. We do NOT auto-retry the
  // emit — re-sending a write could double-apply it; the agent prompt instructs
  // the model to retry the tool, which is the safe place to decide idempotency.
  private static readonly CALL_CONNECT_WAIT_MS = 20_000;
  private static readonly CALL_ACK_TIMEOUT_MS = 30_000;

  // ── Proactive socket recycle ───────────────────────────────────────────
  // Cloud Run severs every WebSocket at its request timeout (3600s is the
  // platform ceiling), so a socket that lives past ~60 minutes is killed at a
  // random moment — historically mid-tool-call, which let the spawned CLI
  // abandon its MCP session. Recycle the transport at a QUIET moment (no
  // in-flight RPC) before the platform deadline instead: an engine-level close
  // looks like a transport drop, so Socket.IO's auto-reconnect and the
  // io "reconnect" → reconnectToSession() recovery path run unchanged. Jitter
  // keeps a fleet of pods from recycling in one thundering herd.
  private static readonly SOCKET_RECYCLE_BASE_MS = 52 * 60 * 1000;
  private static readonly SOCKET_RECYCLE_JITTER_MS = 4 * 60 * 1000;
  private static readonly SOCKET_RECYCLE_BUSY_POLL_MS = 15_000;
  private recycleTimer: NodeJS.Timeout | null = null;
  private pendingCalls = 0;

  async call<M extends keyof AgentSessionServiceMethods>(
    method: M,
    payload: AgentSessionServiceMethods[M]["payload"],
  ): Promise<AgentSessionServiceMethods[M]["response"]> {
    const socket = this.socket;
    if (!socket) {
      throw new Error(
        `Not connected (method: ${String(method)}, session: ${this.config.sessionId})`,
      );
    }
    this.pendingCalls++;
    try {
      if (!socket.connected) {
        // Mid-reconnect — ride it out instead of failing fast.
        await this.waitForConnected(socket, AgentConnection.CALL_CONNECT_WAIT_MS, String(method));
      }
      return await this.emitWithAck(socket, method, payload);
    } finally {
      this.pendingCalls--;
    }
  }

  /** (Re)arm the recycle timer — called on every successful (re)connect. */
  private scheduleSocketRecycle(): void {
    this.clearSocketRecycle();
    const delay =
      AgentConnection.SOCKET_RECYCLE_BASE_MS +
      Math.random() * AgentConnection.SOCKET_RECYCLE_JITTER_MS;
    this.armRecycleTimer(delay);
  }

  private clearSocketRecycle(): void {
    if (this.recycleTimer) {
      clearTimeout(this.recycleTimer);
      this.recycleTimer = null;
    }
  }

  private armRecycleTimer(delay: number): void {
    this.recycleTimer = setTimeout(() => {
      this.recycleTimer = null;
      this.attemptSocketRecycle();
    }, delay);
    // Never hold the process open for a recycle.
    (this.recycleTimer as { unref?: () => void }).unref?.();
  }

  private attemptSocketRecycle(): void {
    const socket = this.socket;
    // Torn down, or the platform sever already beat us — the next "connect"
    // re-arms a fresh cycle.
    if (!socket?.connected) return;
    if (this.pendingCalls > 0) {
      // Busy — poll until quiet. If the platform severs first, we're no worse
      // off than before this existed.
      this.armRecycleTimer(AgentConnection.SOCKET_RECYCLE_BUSY_POLL_MS);
      return;
    }
    process.stderr.write(
      "[conveyor-agent] Recycling socket ahead of the platform request timeout\n",
    );
    (socket.io as { engine?: { close?: () => void } }).engine?.close?.();
  }

  /** Resolve once `socket` reports connected, or reject after `timeoutMs`. */
  private waitForConnected(socket: Socket, timeoutMs: number, method: string): Promise<void> {
    if (socket.connected) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const cleanup = (): void => {
        clearTimeout(timer);
        socket.off("connect", onConnect);
      };
      const onConnect = (): void => {
        cleanup();
        resolve();
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(
          new Error(
            `Not connected — socket did not reconnect within ${timeoutMs / 1000}s ` +
              `(method: ${method}, session: ${this.config.sessionId}). Transient; retry.`,
          ),
        );
      }, timeoutMs);
      socket.once("connect", onConnect);
    });
  }

  /** Emit an RPC and resolve on ack, rejecting if no ack arrives in time. */
  private emitWithAck<M extends keyof AgentSessionServiceMethods>(
    socket: Socket,
    method: M,
    payload: AgentSessionServiceMethods[M]["payload"],
  ): Promise<AgentSessionServiceMethods[M]["response"]> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(
          new Error(
            `Service call timed out after ${AgentConnection.CALL_ACK_TIMEOUT_MS / 1000}s ` +
              `(method: ${String(method)}, session: ${this.config.sessionId}). ` +
              `Usually a transient reconnect; retry.`,
          ),
        );
      }, AgentConnection.CALL_ACK_TIMEOUT_MS);
      socket.emit(
        `agentSessionService:${String(method)}`,
        payload,
        (response: SocketResponse<AgentSessionServiceMethods[M]["response"]>) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (response.success && response.data !== undefined) {
            resolve(response.data);
          } else {
            reject(new Error(response.error ?? `Service call failed: ${String(method)}`));
          }
        },
      );
    });
  }

  // ── Connection lifecycle ───────────────────────────────────────────────

  // oxlint-disable-next-line max-lines-per-function -- socket setup requires registering many co-located event handlers
  connect(): Promise<void> {
    if (!this.config.apiUrl) {
      return Promise.reject(new Error("Cannot connect: apiUrl is empty"));
    }
    this.startProactiveTokenRefresh();
    // oxlint-disable-next-line max-lines-per-function -- socket event registration requires co-located handlers
    return new Promise((resolve, reject) => {
      let settled = false;
      let attempts = 0;
      const maxInitialAttempts = 30;

      process.stderr.write(
        `[conveyor-agent] Connecting to ${this.config.apiUrl} (mode: ${this.config.runnerMode ?? "task"}, session: ${this.config.sessionId})\n`,
      );

      this.socket = io(this.config.apiUrl, {
        auth: {
          taskToken: this.config.taskToken,
          runnerMode: this.config.runnerMode ?? "task",
        },
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 30000,
        randomizationFactor: 0.3,
        extraHeaders: { "ngrok-skip-browser-warning": "true" },
      });

      // ── Server-push event handlers (v7 room-based events) ─────────
      // These events are emitted to the agentSessionService:<sessionId>
      // room. The agent joins this room via the connectAgent() call after
      // the socket connects.

      this.socket.on("session:message", (msg: IncomingMessage) => {
        // Preserve source field from server for critical message filtering
        const incoming: IncomingMessage = {
          content: msg.content,
          userId: msg.userId,
          ...(msg.source && { source: msg.source }),
          ...(msg.files && { files: msg.files }),
          ...(msg.delivery === "prefill" && { delivery: msg.delivery }),
        };
        if (this.messageCallback) this.messageCallback(incoming);
        else this.earlyMessages.push(incoming);
      });

      this.socket.on("session:stop", () => {
        if (this.stopCallback) this.stopCallback();
        else this.earlyStop = true;
      });

      this.socket.on("session:softStop", () => {
        if (this.softStopCallback) this.softStopCallback();
        else this.earlySoftStop = true;
      });

      this.socket.on("session:modeChange", (data: SetModeData) => {
        if (this.modeChangeCallback) this.modeChangeCallback(data);
        else this.earlyModeChanges.push(data);
      });

      this.socket.on(
        "session:answerQuestion",
        (data: { requestId: string; answers: Record<string, string> }) => {
          const resolver = this.pendingAnswerResolvers.get(data.requestId);
          if (resolver) resolver(data.answers);
        },
      );

      this.socket.on("agentRunner:updateApiKey", (data: ApiKeyUpdateData) => {
        if (this.apiKeyUpdateCallback) this.apiKeyUpdateCallback(data);
      });

      this.socket.on("session:pullBranch", (data: { branch: string }) => {
        if (this.pullBranchCallback) this.pullBranchCallback(data);
        else this.earlyPullBranches.push(data);
      });

      // Same-pod review: spawn a child conveyor-agent bound to a fresh review
      // session. Early-buffered — the push can land while the runner is still
      // booting (CI green races pod startup after a wake).
      this.socket.on("session:spawnReview", (data: SpawnReviewData) => {
        if (this.spawnReviewCallback) this.spawnReviewCallback(data);
        else this.earlySpawnReviews.push(data);
      });

      // Same-pod TUI/shell tab: spawn a child conveyor-agent bound to a fresh
      // adhoc/shell session. Early-buffered like spawnReview — the push can
      // land while the runner is still booting.
      this.socket.on("session:spawnTui", (data: SpawnTuiData) => {
        if (this.spawnTuiCallback) this.spawnTuiCallback(data);
        else this.earlySpawnTuis.push(data);
      });

      // The reconciler signals an imminent sleep so the agent captures its
      // finalize snapshot (pg_dump + working tree) NOW — the sleep confirms on
      // this eager capture instead of waiting for the ~2min periodic flush. No
      // early-buffer: if no capture callback is registered yet the pod isn't
      // ready to snapshot, and the shutdown/periodic path is the fallback.
      this.socket.on("session:finalizeSnapshot", () => {
        this.finalizeSnapshotCallback?.();
      });

      this.socket.on("session:runStartCommand", () => {
        this.runStartCommandCallback?.();
      });

      // PTY relay (S2 → agent): keystrokes and reconciled resizes from the S5
      // terminal. Events are room-scoped so each targets this session; the
      // sessionId guard is defensive. No early-buffer — input/resize are
      // meaningless until a PtySession is live and has registered a callback.
      this.socket.on("pty:input", (data: { sessionId?: string; data: string }) => {
        if (data.sessionId && data.sessionId !== this.config.sessionId) return;
        this.ptyInputCallback?.(data.data);
      });

      this.socket.on("pty:resize", (data: { sessionId?: string; cols: number; rows: number }) => {
        if (data.sessionId && data.sessionId !== this.config.sessionId) return;
        this.ptyResizeCallback?.(data.cols, data.rows);
      });

      // ── Socket lifecycle events ────────────────────────────────────
      this.socket.on("connect", () => {
        process.stderr.write("[conveyor-agent] Socket connected\n");
        // Every (re)connect starts a fresh Cloud Run request clock — arm the
        // proactive recycle so the hourly sever lands on our schedule.
        this.scheduleSocketRecycle();
        if (!settled) {
          settled = true;
          resolve();
        }
      });

      this.socket.on("connect_error", (err: Error) => {
        attempts++;
        process.stderr.write(
          `[conveyor-agent] Connection error (attempt ${attempts}/${maxInitialAttempts}): ${err.message}\n`,
        );
        if (!settled && attempts >= maxInitialAttempts) {
          settled = true;
          reject(
            new Error(
              `Failed to connect to ${this.config.apiUrl} after ${maxInitialAttempts} attempts: ${err.message}`,
            ),
          );
        }
      });

      this.socket.on("disconnect", (reason: string) => {
        process.stderr.write(`[conveyor-agent] Disconnected: ${reason}\n`);
        // Server-initiated disconnect (auth rejected, session terminated)
        // arrives with reason "server namespace disconnect" or
        // "io server disconnect". The token may be stale — try refreshing
        // before Socket.IO's transport-level reconnect kicks in.
        if (reason === "io server disconnect" || reason === "server namespace disconnect") {
          void this.refreshTaskTokenFromBootstrap()
            .then((refreshed) => {
              if (refreshed && this.socket) {
                // socket.io's `io server disconnect` does NOT auto-reconnect
                // — we have to nudge it manually after refreshing the token.
                this.socket.connect();
              }
            })
            .catch(() => {});
        }
      });

      this.socket.on("auth:rejected", () => {
        process.stderr.write("[conveyor-agent] Auth rejected by server, refreshing taskToken\n");
        void this.refreshTaskTokenFromBootstrap().catch(() => {});
      });

      this.socket.io.on("reconnect", (reconnectAttempts: number) => {
        process.stderr.write(
          `[conveyor-agent] Reconnected (attempts: ${reconnectAttempts}, ${new Date().toISOString()})\n`,
        );
        // Renew the session lease IMMEDIATELY — a starvation-induced strand
        // recovers the instant the loop unbricks (Stranded → Active), instead
        // of waiting out the up-to-30s heartbeat timer against the 120s grace.
        this.sendHeartbeat();
        // Re-join the session room and process any pending messages from the disconnect window
        void this.reconnectToSession();
      });

      this.socket.io.on("reconnect_attempt", () => {
        // Attempt counting is handled in connect_error handler above
      });
    });
  }

  disconnect(): void {
    this.stopProactiveTokenRefresh();
    this.clearSocketRecycle();
    this.stopHeartbeatWorker();
    // Best-effort flush on disconnect — don't await
    void this.flushEvents();
    if (this.socket) {
      this.socket.io.reconnection(false);
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // ── Reconnect with retry ────────────────────────────────────────────
  //
  // Socket.IO already retries the transport forever. This higher-level helper
  // re-issues the `connectAgent` RPC after a successful reconnect to re-join
  // the session room and drain pending messages. We retry indefinitely with a
  // capped exponential backoff — a stranded codespace with a missing agent is
  // worse than a long-running reconnect loop, and a transient API outage
  // shouldn't kill the agent process.

  private static readonly RECONNECT_BASE_DELAY_MS = 2_000;
  private static readonly RECONNECT_MAX_DELAY_MS = 60_000;
  private static readonly RECONNECT_STATUS_EVERY_N = 3;

  private isReconnecting = false;

  /**
   * Invoked after every successful session reconnect (the `connectAgent` RPC
   * re-established the session room). The runner uses this to force a TUI
   * repaint: the reconnect may have landed on a different/restarted API
   * process whose PTY scrollback ring is empty, and a quiet terminal would
   * otherwise never re-seed it.
   */
  onReconnected?: () => void;

  private async reconnectToSession(): Promise<void> {
    if (this.isReconnecting) return;
    this.isReconnecting = true;
    try {
      let attempt = 0;
      while (this.socket) {
        attempt++;
        try {
          const { pendingMessages } = await this.call("connectAgent", {
            sessionId: this.config.sessionId,
          });
          this.drainPendingMessages(pendingMessages);
          process.stderr.write(
            `[conveyor-agent] Reconnected to session successfully (attempts: ${attempt})\n`,
          );
          // Only re-report status if it actually changed since the last
          // successful report — avoids spamming duplicate status transitions
          // through the system on every reconnect.
          if (this.lastEmittedStatus && this.lastEmittedStatus !== this.lastReportedStatus) {
            const status = this.lastEmittedStatus;
            void this.call("reportAgentStatus", {
              sessionId: this.config.sessionId,
              status,
            })
              .then(() => {
                this.lastReportedStatus = status;
              })
              .catch(() => {});
          }
          // Notify UI subscribers that reconnect succeeded.
          this.sendEvent({
            type: "agent_runner_status",
            reason: "reconnected",
            attempts: attempt,
          });
          try {
            this.onReconnected?.();
          } catch {
            /* repaint is best-effort — never fail the reconnect over it */
          }
          return;
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err);
          // Capped exponential backoff: 2s, 4s, 8s, 16s, 32s, then 60s steady.
          const delayMs = Math.min(
            AgentConnection.RECONNECT_BASE_DELAY_MS * 2 ** Math.min(attempt - 1, 5),
            AgentConnection.RECONNECT_MAX_DELAY_MS,
          );
          process.stderr.write(
            `[conveyor-agent] connectAgent failed (attempt ${attempt}): ${errMsg} — retrying in ${delayMs / 1000}s\n`,
          );

          // Auth-rejected on the connectAgent RPC — the taskToken may be
          // stale (e.g. after a 24h JWT TTL or a server-side rotation). Try
          // refreshing from the bootstrap endpoint before the next attempt.
          if (this.looksLikeAuthError(errMsg)) {
            void this.refreshTaskTokenFromBootstrap().catch(() => {});
          }

          // Every Nth failure, emit a status event so the UI shows we're
          // still trying — piggybacks on the existing `task:agentStatus`
          // pathway via sendEvent (no new socket event needed).
          if (attempt % AgentConnection.RECONNECT_STATUS_EVERY_N === 0) {
            this.sendEvent({
              type: "agent_runner_status",
              reason: "reconnecting",
              attempt,
            });
          }

          await new Promise<void>((resolve) => {
            setTimeout(resolve, delayMs);
          });
        }
      }
    } finally {
      this.isReconnecting = false;
    }
  }

  private looksLikeAuthError(message: string): boolean {
    // "session not found" / "session expired" / "invalid session" are the
    // server's wording for an evicted-or-expired task session — the symptom of
    // a stale token. They contain none of unauthor/forbid/auth/token, so match
    // them explicitly, otherwise the refresh-on-reconnect path never fires.
    return /unauthor|forbid|auth|token|session (?:not found|expired|invalid)|invalid session/i.test(
      message,
    );
  }

  // ── Proactive task-token refresh ────────────────────────────────────────
  //
  // Socket.IO only re-presents the taskToken on a (re)connect handshake, and
  // the server only re-validates the JWT then. So a token that expires while
  // the socket stays connected goes unnoticed until the next RPC fails. Re-mint
  // periodically from the bootstrap endpoint — refreshFromBootstrap() updates
  // both this.config.taskToken and socket.auth.taskToken, so any later
  // reconnect carries a fresh token. No-ops for project mode / missing
  // codespace env, and is rate-limited to once/60s inside refreshFromBootstrap.

  private startProactiveTokenRefresh(): void {
    if (this.tokenRefreshTimer) return;
    this.tokenRefreshTimer = setInterval(() => {
      void this.refreshTaskTokenFromBootstrap().catch(() => {});
    }, TOKEN_REFRESH_INTERVAL_MS);
    // Don't let the refresh timer keep the process alive on its own.
    this.tokenRefreshTimer.unref?.();
  }

  private stopProactiveTokenRefresh(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  private drainPendingMessages(messages: Array<{ content: string; userId: string }>): void {
    for (const msg of messages) {
      if (!msg.content) continue;
      if (this.messageCallback) {
        this.messageCallback({ content: msg.content, userId: msg.userId });
      } else {
        this.earlyMessages.push({ content: msg.content, userId: msg.userId });
      }
    }
  }

  // ── Callback registration with early-buffer draining ───────────────

  onMessage(callback: (msg: IncomingMessage) => void): void {
    this.messageCallback = callback;
    for (const msg of this.earlyMessages) callback(msg);
    this.earlyMessages = [];
  }

  onStop(callback: () => void): void {
    this.stopCallback = callback;
    if (this.earlyStop) {
      callback();
      this.earlyStop = false;
    }
  }

  onSoftStop(callback: () => void): void {
    this.softStopCallback = callback;
    if (this.earlySoftStop) {
      callback();
      this.earlySoftStop = false;
    }
  }

  onModeChange(callback: (data: SetModeData) => void): void {
    this.modeChangeCallback = callback;
    for (const data of this.earlyModeChanges) callback(data);
    this.earlyModeChanges = [];
  }

  onApiKeyUpdate(callback: (data: ApiKeyUpdateData) => void): void {
    this.apiKeyUpdateCallback = callback;
  }

  onPullBranch(callback: (data: { branch: string }) => void): void {
    this.pullBranchCallback = callback;
    for (const data of this.earlyPullBranches) callback(data);
    this.earlyPullBranches = [];
  }

  onSpawnReview(callback: (data: SpawnReviewData) => void): void {
    this.spawnReviewCallback = callback;
    for (const data of this.earlySpawnReviews) callback(data);
    this.earlySpawnReviews = [];
  }

  /**
   * Report that a same-pod review child failed to spawn (fire-and-forget).
   * The server Ends the orphaned review session and falls back to a dedicated
   * review pod. sessionId is OUR (builder) session — the SEC-9 guard runs on
   * it; the review session is identified separately.
   */
  reportReviewSpawnFailure(reviewSessionId: string, error?: string): void {
    if (!this.socket) return;
    void this.call("reportReviewSpawnFailure", {
      sessionId: this.config.sessionId,
      reviewSessionId,
      ...(error ? { error: error.slice(0, 2000) } : {}),
    }).catch(() => {});
  }

  onSpawnTui(callback: (data: SpawnTuiData) => void): void {
    this.spawnTuiCallback = callback;
    for (const data of this.earlySpawnTuis) callback(data);
    this.earlySpawnTuis = [];
  }

  /**
   * Report that a same-pod TUI/shell child failed to spawn (fire-and-forget).
   * The server Ends the orphaned session — no fallback pod (unlike review).
   * sessionId is OUR (builder) session — the SEC-9 guard runs on it.
   */
  reportSessionSpawnFailure(spawnedSessionId: string, error?: string): void {
    if (!this.socket) return;
    void this.call("reportSessionSpawnFailure", {
      sessionId: this.config.sessionId,
      spawnedSessionId,
      ...(error ? { error: error.slice(0, 2000) } : {}),
    }).catch(() => {});
  }

  onFinalizeSnapshot(callback: () => void): void {
    this.finalizeSnapshotCallback = callback;
  }

  onRunStartCommand(callback: () => void): void {
    this.runStartCommandCallback = callback;
  }

  // ── PTY relay (S5 Connected-TUI terminal) ──────────────────────────

  /**
   * Forward a raw chunk of terminal output to the S2 relay (fire-and-forget).
   * The first chunk creates the server-side scrollback ring, which is what
   * surfaces the terminal in the UI. `dims` seed/refresh the ring geometry.
   */
  sendPtyOutput(data: string, dims?: { cols: number; rows: number }): void {
    if (!this.socket) return;
    void this.call("ptyOutput", {
      sessionId: this.config.sessionId,
      data,
      ...(dims ? { cols: dims.cols, rows: dims.rows } : {}),
    }).catch(() => {});
  }

  /**
   * Forward one compact chat-proxy event derived from the transcript JSONL to
   * the relay (fire-and-forget). Feeds the experimental chat PTY proxy ring.
   * Old servers that don't know the method reject harmlessly.
   */
  sendPtyChatEvent(event: PtyChatEventPayload): void {
    if (!this.socket) return;
    void this.call("ptyChatEvent", {
      sessionId: this.config.sessionId,
      event,
    }).catch(() => {});
  }

  /**
   * Report that the interactive CLI process for this session has died and no
   * respawn is imminent (fire-and-forget). The server clears the scrollback
   * ring and broadcasts pty:ended so clients hide the Connected-TUI tab. Old
   * servers that don't know the method reject harmlessly.
   */
  sendPtyEnded(): void {
    if (!this.socket) return;
    void this.call("ptyEnded", { sessionId: this.config.sessionId }).catch(() => {});
  }

  /** Subscribe to relayed keystrokes. Returns an unsubscribe fn. */
  onPtyInput(handler: (data: string) => void): () => void {
    this.ptyInputCallback = handler;
    return () => {
      if (this.ptyInputCallback === handler) this.ptyInputCallback = null;
    };
  }

  /** Subscribe to relayed (reconciled) terminal resizes. Returns an unsubscribe fn. */
  onPtyResize(handler: (cols: number, rows: number) => void): () => void {
    this.ptyResizeCallback = handler;
    return () => {
      if (this.ptyResizeCallback === handler) this.ptyResizeCallback = null;
    };
  }

  // ── Convenience methods (thin wrappers around call / emit) ─────────

  async emitStatus(status: string, reason?: string, questionText?: string): Promise<void> {
    this.lastEmittedStatus = status;
    // Await event flush (e.g. "completed") before reporting status change
    // so the API processes "completed" before "idle" — eliminates the
    // ordering race that caused duplicate completion message spam.
    await this.flushEvents();
    const payload = {
      sessionId: this.config.sessionId,
      status,
      ...(reason ? { reason } : {}),
      // Only sent with a pending TUI questionnaire (reason "user_question") so
      // the server can surface the real question text in the notification.
      ...(questionText ? { questionText } : {}),
    };
    const AWAIT_STATUSES = ["idle", "waiting_for_input", "connected"];
    if (AWAIT_STATUSES.includes(status)) {
      // Critical transition — server must process before agent blocks on waitForMessage()
      try {
        await this.call("reportAgentStatus", payload);
        this.lastReportedStatus = status;
      } catch {
        // Best-effort — proceed even if status report fails.
        // Server-side forwardOrQueue has a DB cross-check fallback.
      }
    } else {
      void this.call("reportAgentStatus", payload)
        .then(() => {
          this.lastReportedStatus = status;
        })
        .catch(() => {});
    }
  }

  postChatMessage(content: string): void {
    if (!this.socket) return;
    if (this.suppressIfDuplicate(content)) return;
    void this.call("postAgentMessage", {
      sessionId: this.config.sessionId,
      content,
    }).catch(() => {});
  }

  // Awaitable variant of postChatMessage for callers that need to guarantee
  // the message is acknowledged by the server before proceeding (e.g. before
  // aborting the session). Dedup still applies; a suppressed message resolves
  // immediately without hitting the wire.
  async postChatMessageAwait(content: string): Promise<void> {
    if (!this.socket) return;
    if (this.suppressIfDuplicate(content)) return;
    try {
      await this.call("postAgentMessage", {
        sessionId: this.config.sessionId,
        content,
      });
    } catch (err) {
      process.stderr.write(
        `[conveyor-agent] postChatMessageAwait failed: ${err instanceof Error ? err.message : String(err)}\n`,
      );
    }
  }

  private suppressIfDuplicate(content: string): boolean {
    const d = this.checkAndTrackDuplicate(content);
    if (!d.duplicate) return false;
    process.stderr.write(
      `[dedup] Suppressed near-duplicate (matched: "${d.matchedMessagePreview}")\n`,
    );
    return true;
  }

  // Exposed so `post_to_chat` can surface suppression back to the agent.
  checkAndTrackDuplicate(
    content: string,
  ): { duplicate: false } | { duplicate: true; matchedMessagePreview: string } {
    const now = Date.now();
    this.recentMessages = this.recentMessages.filter(
      (m) => now - m.timestamp < AgentConnection.DEDUP_WINDOW_MS,
    );
    const words = new Set(
      content
        .toLowerCase()
        .replace(/[^\w\s]/g, "")
        .split(/\s+/)
        .filter((w) => w.length >= 3),
    );
    if (words.size === 0) return { duplicate: false };
    for (const recent of this.recentMessages) {
      let intersection = 0;
      for (const w of words) if (recent.words.has(w)) intersection++;
      const union = new Set([...words, ...recent.words]).size;
      if (union > 0 && intersection / union > AgentConnection.DEDUP_SIMILARITY_THRESHOLD) {
        return { duplicate: true, matchedMessagePreview: recent.preview };
      }
    }
    const max = AgentConnection.DEDUP_PREVIEW_LIMIT;
    const preview = content.length > max ? content.slice(0, max) + "…" : content;
    this.recentMessages.push({ words, timestamp: now, preview });
    if (this.recentMessages.length > 3) this.recentMessages.shift();
    return { duplicate: false };
  }

  sendHeartbeat(loopLagMs?: number): void {
    if (!this.socket) return;
    const statusMap: Record<string, "active" | "idle" | "building"> = {
      running: "active",
      fetching_context: "active",
      connected: "active",
      connecting: "active",
      idle: "idle",
    };
    const heartbeatStatus = statusMap[this.lastEmittedStatus ?? "idle"] ?? "active";
    void this.call("heartbeat", {
      sessionId: this.config.sessionId,
      timestamp: new Date().toISOString(),
      status: heartbeatStatus,
      ...(loopLagMs !== undefined && loopLagMs > 0 ? { loopLagMs: Math.round(loopLagMs) } : {}),
    }).catch(() => {});
  }

  // ── Starvation-proof heartbeat worker ────────────────────────────────
  //
  // A worker thread with its own event loop + Socket.IO connection keeps the
  // v3 session lease renewed even when the MAIN loop is stalled (the failure
  // mode where a heavy gate got the session declared stranded and restarted
  // mid-run). Best-effort by design: any spawn/runtime failure just degrades
  // heartbeats to main-loop-only. See heartbeat-worker.ts for the policy.
  private heartbeatWorker: Worker | null = null;

  startHeartbeatWorker(sharedBuffer: SharedArrayBuffer, intervalMs = 30_000): void {
    if (this.heartbeatWorker) return;
    try {
      const workerUrl = new URL("./heartbeat-worker.js", import.meta.url);
      // Under vitest/tsx the sibling is the .ts source — no compiled worker to
      // spawn. dist builds emit heartbeat-worker.js next to index/cli bundles.
      if (!existsSync(fileURLToPath(workerUrl))) {
        process.stderr.write(
          "[conveyor-agent] heartbeat worker bundle not found — main-loop heartbeat only\n",
        );
        return;
      }
      const worker = new Worker(workerUrl, {
        workerData: {
          apiUrl: this.config.apiUrl,
          taskToken: this.config.taskToken,
          sessionId: this.config.sessionId,
          runnerMode: this.config.runnerMode ?? "task",
          sharedBuffer,
          intervalMs,
        },
      });
      worker.unref();
      worker.on("error", (err) => {
        process.stderr.write(`[conveyor-agent] heartbeat worker error: ${err.message}\n`);
        this.heartbeatWorker = null;
      });
      worker.on("exit", (code) => {
        if (code !== 0) {
          process.stderr.write(`[conveyor-agent] heartbeat worker exited (code ${code})\n`);
        }
        this.heartbeatWorker = null;
      });
      this.heartbeatWorker = worker;
      process.stderr.write("[conveyor-agent] heartbeat worker started\n");
    } catch (err) {
      process.stderr.write(
        `[conveyor-agent] heartbeat worker failed to start: ${err instanceof Error ? err.message : String(err)}\n`,
      );
      this.heartbeatWorker = null;
    }
  }

  stopHeartbeatWorker(): void {
    const worker = this.heartbeatWorker;
    this.heartbeatWorker = null;
    if (worker) void worker.terminate();
  }

  emitModeChanged(agentMode?: AgentMode | null): void {
    this.sendEvent({ type: "mode_changed", agentMode });
  }

  async updateTaskFields(fields: {
    plan?: string;
    description?: string;
  }): Promise<{ ok: boolean; error?: string }> {
    if (!this.socket) return { ok: false, error: "socket not connected" };
    try {
      await this.call("updateTaskFields", { sessionId: this.config.sessionId, ...fields });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  }

  storeSessionId(sdkSessionId: string): void {
    void this.call("storeSessionId", { sessionId: this.config.sessionId, sdkSessionId }).catch(
      () => {},
    );
  }

  /** Report the full current set of runtime-discovered listening ports.
   *  Throws on failure so the PortDiscovery poller can retry on its next
   *  tick (a swallowed error here would silently drop the delta). */
  async reportDiscoveredPorts(ports: WorkspaceDiscoveredPort[]): Promise<void> {
    await this.call("reportDiscoveredPorts", { sessionId: this.config.sessionId, ports });
  }

  // ── Typing indicators ───────────────────────────────────────────────

  sendTypingStart(): void {
    this.sendEvent({ type: "agent_typing_start" });
  }

  sendTypingStop(): void {
    this.sendEvent({ type: "agent_typing_stop" });
  }

  // ── RPC convenience wrappers (v6 compat, will migrate to call()) ───

  emitRateLimitPause(resetsAt: string): void {
    this.sendEvent({ type: "rate_limit_update", resetsAt });
  }

  updateStatus(status: string): void {
    this.emitStatus(status);
  }

  emitCodeReviewResult(content: string, approved: boolean): void {
    if (!this.socket) return;
    void this.call("submitCodeReviewResult", {
      sessionId: this.config.sessionId,
      content,
      approved,
    }).catch(() => {});
  }

  /**
   * The session's key hit a hard usage cap — ask the server to stamp it
   * limited and hand back the best remaining key's credential env (or a
   * requeue confirmation when none is left). Awaited: the caller swaps
   * credentials and resumes on success, so it needs the real response.
   */
  async cycleCodingAgentKey(
    rateLimitType: string,
    resetsAt?: string,
  ): Promise<
    | { cycled: true; label: string; envVars: Record<string, string> }
    | { cycled: false; resetsAt: string }
  > {
    return await this.call("cycleCodingAgentKey", {
      sessionId: this.config.sessionId,
      rateLimitType,
      ...(resetsAt ? { resetsAt } : {}),
    });
  }

  // ── Question handling ──────────────────────────────────────────────

  async askUserQuestion(questions: AgentQuestion[]): Promise<Record<string, string>> {
    const questionText = questions
      .map(
        (q) =>
          `**${q.header}**\n${q.question}${q.options.length ? "\n" + q.options.map((o) => `- ${o.label}: ${o.description}`).join("\n") : ""}`,
      )
      .join("\n\n");

    const requestId = crypto.randomUUID();

    // Race the RPC callback against the room-event fallback.
    // If the socket disconnects/reconnects while waiting, the RPC callback is
    // lost but the room event (emitted after reconnect to the session room)
    // will still deliver the answer.
    const roomEventPromise = new Promise<Record<string, string>>((resolve) => {
      this.pendingAnswerResolvers.set(requestId, resolve);
    });

    const rpcPromise = this.call("askUserQuestion", {
      sessionId: this.config.sessionId,
      question: questionText,
      requestId,
      questions,
    }).then((res) => res.answers);

    try {
      return await Promise.race([rpcPromise, roomEventPromise]);
    } finally {
      this.pendingAnswerResolvers.delete(requestId);
    }
  }

  // ── Typed service method wrappers ───────────────────────────────────

  getTaskProperties(): Promise<{
    plan?: string;
    storyPointId?: string;
    title?: string;
  }> {
    return this.call("getTaskProperties", { sessionId: this.config.sessionId });
  }

  triggerIdentification(): Promise<{ identified: boolean }> {
    return this.call("triggerIdentification", { sessionId: this.config.sessionId });
  }

  async refreshAuthToken(): Promise<boolean> {
    const result = await this.refreshFromBootstrap();
    return result.refreshedClaude;
  }

  /**
   * Refresh the in-process `CONVEYOR_TASK_TOKEN` from the bootstrap endpoint.
   * Returns true if a new token was applied. Rate-limited locally to once per
   * 60s so a tight auth-rejected loop can't hammer the bootstrap endpoint —
   * the server enforces the same window via `lastBootstrapAt`.
   */
  private lastTaskTokenRefreshAt = 0;
  async refreshTaskTokenFromBootstrap(): Promise<boolean> {
    const result = await this.refreshFromBootstrap();
    return result.refreshedTaskToken;
  }

  private refreshFromBootstrap(): Promise<{
    refreshedClaude: boolean;
    refreshedTaskToken: boolean;
  }> {
    const none = Promise.resolve({ refreshedClaude: false, refreshedTaskToken: false });
    // Claudespace pods carry POD_BOOTSTRAP_TOKEN (retained by the entrypoint as
    // the credential-refresh key); the legacy GitHub Codespaces path keys on
    // instance name + CONVEYOR_BOOTSTRAP_TOKEN.
    const podBootstrapToken = process.env.POD_BOOTSTRAP_TOKEN;
    const codespaceName = process.env.CODESPACE_NAME || process.env.CLAUDESPACE_NAME;
    const apiUrl = this.config.apiUrl;
    if (!apiUrl || (!podBootstrapToken && !codespaceName)) {
      return none;
    }
    const now = Date.now();
    if (now - this.lastTaskTokenRefreshAt < 60_000) {
      return none;
    }
    this.lastTaskTokenRefreshAt = now;
    if (podBootstrapToken) {
      return this.refreshFromV3Bootstrap(apiUrl, podBootstrapToken);
    }
    if (!codespaceName) return none;
    return this.refreshFromCodespaceBootstrap(apiUrl, codespaceName);
  }

  /** Legacy GitHub Codespaces refresh path — keys on instance name. */
  private async refreshFromCodespaceBootstrap(
    apiUrl: string,
    codespaceName: string,
  ): Promise<{ refreshedClaude: boolean; refreshedTaskToken: boolean }> {
    const bootstrapToken = process.env.CONVEYOR_BOOTSTRAP_TOKEN;
    const result = await fetchBootstrap({
      apiUrl,
      instanceName: codespaceName,
      bootstrapToken,
      // Do not retry on http errors during a runtime refresh — a 401/403
      // means the token is consumed / session terminal and retrying won't
      // help. Network/timeout still retry inside fetchBootstrap.
    });
    if (!result.ok) return { refreshedClaude: false, refreshedTaskToken: false };
    const previousTaskToken = process.env.CONVEYOR_TASK_TOKEN;
    applyBootstrapToEnv(result.config);
    const refreshedTaskToken =
      result.config.mode !== "project" &&
      Boolean(result.config.taskToken) &&
      result.config.taskToken !== previousTaskToken;
    if (refreshedTaskToken && result.config.taskToken) {
      this.config.taskToken = result.config.taskToken;
      // Update socket auth so the next reconnect cycle uses the fresh token.
      if (this.socket) {
        const auth = this.socket.auth as Record<string, unknown> | undefined;
        if (auth && typeof auth === "object") {
          auth.taskToken = result.config.taskToken;
        }
      }
      this.heartbeatWorker?.postMessage({ taskToken: result.config.taskToken });
    }
    const refreshedClaude = Boolean(result.config.envVars?.CLAUDE_CODE_OAUTH_TOKEN);
    return { refreshedClaude, refreshedTaskToken };
  }

  /**
   * v3 refresh: re-fetch the full bootstrap bundle from the pod's bound v3
   * route and swap the credentials in place. The GitHub installation token
   * dies at ~1h and the sessionJwt at 24h; re-polling the bootstrap GET with
   * the same pod token is the designed refresh mechanism.
   */
  private async refreshFromV3Bootstrap(
    apiUrl: string,
    bootstrapToken: string,
  ): Promise<{ refreshedClaude: boolean; refreshedTaskToken: boolean }> {
    // maxWaitMs 0: a bound pod answers 200 on the first request; a 204
    // (unbound — should not happen at refresh time) or any HTTP/network error
    // must fail the refresh, not park the agent in a poll loop. The one
    // exception is a transient 429 (shared Cloud NAT IP vs podBootstrapLimiter),
    // which pollBundleWithRateLimitRetry retries before giving up.
    const bundle = await this.pollBundleWithRateLimitRetry(apiUrl, bootstrapToken);
    if (!bundle) {
      return { refreshedClaude: false, refreshedTaskToken: false };
    }

    const previousTaskToken = process.env.CONVEYOR_TASK_TOKEN;
    for (const [key, value] of Object.entries(bundle.envVars ?? {})) {
      process.env[key] = value;
    }
    if (bundle.githubToken) process.env.CONVEYOR_GITHUB_TOKEN = bundle.githubToken;
    if (bundle.anthropicKey) process.env.ANTHROPIC_API_KEY = bundle.anthropicKey;
    if (bundle.gcpToken) process.env.CLOUDSDK_AUTH_ACCESS_TOKEN = bundle.gcpToken;

    const refreshedTaskToken =
      Boolean(bundle.sessionJwt) && bundle.sessionJwt !== previousTaskToken;
    if (refreshedTaskToken) {
      process.env.CONVEYOR_TASK_TOKEN = bundle.sessionJwt;
      this.config.taskToken = bundle.sessionJwt;
      // Update socket auth so the next reconnect cycle uses the fresh token.
      if (this.socket) {
        const auth = this.socket.auth as Record<string, unknown> | undefined;
        if (auth && typeof auth === "object") {
          auth.taskToken = bundle.sessionJwt;
        }
      }
      this.heartbeatWorker?.postMessage({ taskToken: bundle.sessionJwt });
    }
    const refreshedClaude = Boolean(bundle.envVars?.CLAUDE_CODE_OAUTH_TOKEN);
    return { refreshedClaude, refreshedTaskToken };
  }

  /**
   * Poll the bootstrap bundle once (maxWaitMs 0), retrying only on a transient
   * 429 (standby-pool pods share one Cloud NAT IP against podBootstrapLimiter)
   * with a short backoff. Returns null when the refresh should be abandoned —
   * a non-429 error, or 429s past the retry budget — so the caller no-ops
   * instead of parking a RUNNING pod in a poll loop.
   */
  private async pollBundleWithRateLimitRetry(
    apiUrl: string,
    bootstrapToken: string,
  ): Promise<Awaited<ReturnType<typeof pollUntilBound>> | null> {
    const retryDelaysMs = [1_000, 3_000];
    for (let attempt = 0; ; attempt++) {
      try {
        return await pollUntilBound({ apiUrl, bootstrapToken, maxWaitMs: 0 });
      } catch (err) {
        const isRateLimited = err instanceof PollUntilBoundHttpError && err.status === 429;
        if (!isRateLimited || attempt >= retryDelaysMs.length) {
          return null;
        }
        await new Promise<void>((resolve) => {
          setTimeout(resolve, retryDelaysMs[attempt]);
        });
      }
    }
  }

  // ── Event buffering ────────────────────────────────────────────────

  sendEvent(event: { type: string; [key: string]: unknown }): void {
    if (!this.socket) return;
    // Cap the buffer so a long disconnect can't accumulate an unbounded
    // replay storm. Drop oldest on overflow (keeps the most recent state).
    if (this.eventBuffer.length >= MAX_EVENT_BUFFER) {
      this.eventBuffer.shift();
      this.droppedEventCount++;
      if (this.droppedEventCount === 1 || this.droppedEventCount % 500 === 0) {
        process.stderr.write(
          `[conveyor-agent] eventBuffer overflow — dropped ${this.droppedEventCount} event(s) (cap: ${MAX_EVENT_BUFFER})\n`,
        );
      }
    }
    this.eventBuffer.push({ event });
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => void this.flushEvents(), EVENT_BATCH_MS);
    }
  }

  async flushEvents(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (!this.socket || this.eventBuffer.length === 0) return;
    const events = this.eventBuffer.map((entry) => entry.event);
    this.eventBuffer = [];
    // Await so callers (emitStatus) can guarantee ordering
    await this.call("emitAgentEvent", { sessionId: this.config.sessionId, events }).catch(() => {});
  }
}
